import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Save, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BulkReadingsPage = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState(12);
  const [values, setValues] = useState({});

  const MONTHS = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος',
    'Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];

  useEffect(() => { fetchData(); }, [year, month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/readings/bulk-entry/${year}/${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMachines(res.data);
      const initial = {};
      res.data.forEach(m => {
        if (m.existing) {
          initial[m.machine_id] = {
            a4_black: m.existing.counter_value_black || 0,
            a4_color: m.existing.counter_value_color || 0,
            a3_black: m.existing.counter_value_a3 || 0,
            a3_color: m.existing.counter_value_a3_color || 0,
            done: true
          };
        } else {
          initial[m.machine_id] = { a4_black: '', a4_color: '', a3_black: '', a3_color: '', done: false };
        }
      });
      setValues(initial);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (machineId, field, val) => {
    setValues(prev => ({
      ...prev,
      [machineId]: { ...prev[machineId], [field]: val }
    }));
  };

  const handleSave = async () => {
    const readings = [];
    for (const m of machines) {
      const v = values[m.machine_id];
      if (!v || v.done) continue;
      const a4b = parseInt(v.a4_black) || 0;
      const a4c = parseInt(v.a4_color) || 0;
      const a3b = parseInt(v.a3_black) || 0;
      const a3c = parseInt(v.a3_color) || 0;
      if (a4b === 0 && a4c === 0 && a3b === 0 && a3c === 0) continue;
      if (a4b > 0 && a4b < m.prev_black) {
        toast.error(`${m.serial}: A4 Black (${a4b}) < Προηγ. (${m.prev_black})`);
        return;
      }
      if (a4c > 0 && a4c < m.prev_color) {
        toast.error(`${m.serial}: A4 Color (${a4c}) < Προηγ. (${m.prev_color})`);
        return;
      }
      if (a3b > 0 && a3b < m.prev_a3) {
        toast.error(`${m.serial}: A3 Black (${a3b}) < Προηγ. (${m.prev_a3})`);
        return;
      }
      if (a3c > 0 && a3c < m.prev_a3_color) {
        toast.error(`${m.serial}: A3 Color (${a3c}) < Προηγ. (${m.prev_a3_color})`);
        return;
      }
      readings.push({
        machine_id: m.machine_id,
        counter_value_black: a4b,
        counter_value_color: a4c,
        counter_value_a3: a3b,
        counter_value_a3_color: a3c
      });
    }

    if (readings.length === 0) {
      toast.error('Δεν υπάρχουν μετρήσεις για αποθήκευση');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/readings/bulk`, {
        year, month, readings
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = machines.filter(m => {
    const v = values[m.machine_id];
    if (!v || v.done) return false;
    return (parseInt(v.a4_black) > 0 || parseInt(v.a4_color) > 0 || parseInt(v.a3_black) > 0 || parseInt(v.a3_color) > 0);
  }).length;
  const doneCount = machines.filter(m => values[m.machine_id]?.done).length;

  if (loading) return <div className="flex justify-center py-12">Φόρτωση...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Ενημέρωση Μετρητών Μηχανημάτων</h1>
          <p className="text-sm text-gray-600">Ενημέρωση αρχικών μετρητών - ΔΕΝ επηρεάζει σελίδες/σύνολα μετρήσεων</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={month} onChange={e => setMonth(Number(e.target.value))}
            className="h-10 px-3 border rounded-md" data-testid="bulk-month">
            {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <Input type="number" value={year} onChange={e => setYear(Number(e.target.value))}
            className="w-24" data-testid="bulk-year" />
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-sm">
        <span className="flex items-center gap-1 text-blue-600">
          <FileText className="h-4 w-4" /> {machines.length} μηχανήματα
        </span>
        {pendingCount > 0 && (
          <span className="flex items-center gap-1 text-orange-600">
            <AlertCircle className="h-4 w-4" /> {pendingCount} προς ενημέρωση
          </span>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#004a99] text-white sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left whitespace-nowrap" rowSpan={2}>ΠΕΛΑΤΗΣ</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap" rowSpan={2}>S/N</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap" rowSpan={2}>ΜΟΝΤΕΛΟ</th>
                  <th className="px-2 py-2 text-center border-l border-white/30" colSpan={2}>A4 BLACK</th>
                  <th className="px-2 py-2 text-center border-l border-white/30" colSpan={2}>A4 COLOR</th>
                  <th className="px-2 py-2 text-center border-l border-white/30" colSpan={2}>A3 BLACK</th>
                  <th className="px-2 py-2 text-center border-l border-white/30" colSpan={2}>A3 COLOR</th>
                  <th className="px-2 py-2 text-center border-l border-white/30" rowSpan={2}>STATUS</th>
                </tr>
                <tr>
                  <th className="px-1 py-1 text-center text-[10px] border-l border-white/30">ΤΡΕΧΩΝ</th>
                  <th className="px-1 py-1 text-center text-[10px]">ΝΕΟΣ</th>
                  <th className="px-1 py-1 text-center text-[10px] border-l border-white/30">ΤΡΕΧΩΝ</th>
                  <th className="px-1 py-1 text-center text-[10px]">ΝΕΟΣ</th>
                  <th className="px-1 py-1 text-center text-[10px] border-l border-white/30">ΤΡΕΧΩΝ</th>
                  <th className="px-1 py-1 text-center text-[10px]">ΝΕΟΣ</th>
                  <th className="px-1 py-1 text-center text-[10px] border-l border-white/30">ΤΡΕΧΩΝ</th>
                  <th className="px-1 py-1 text-center text-[10px]">ΝΕΟΣ</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {machines.map((m, idx) => {
                  const v = values[m.machine_id] || {};
                  const isDone = v.done;
                  return (
                    <tr key={m.machine_id} className={isDone ? 'bg-green-50' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                      <td className="px-2 py-1 text-xs font-medium max-w-[130px] truncate">{m.customer}</td>
                      <td className="px-2 py-1 text-xs font-mono">{m.serial}</td>
                      <td className="px-2 py-1 text-xs">{m.brand} {m.model}</td>
                      {/* A4 BLACK */}
                      <td className="px-1 py-1 text-center text-xs text-gray-500 border-l">{m.prev_black.toLocaleString('el-GR')}</td>
                      <td className="px-1 py-1">
                        {isDone ? (
                          <div className="text-center font-mono text-xs text-green-700">{(v.a4_black || 0).toLocaleString('el-GR')}</div>
                        ) : (
                          <Input type="number" value={v.a4_black} placeholder={String(m.prev_black)}
                            onChange={e => handleChange(m.machine_id, 'a4_black', e.target.value)}
                            className="h-7 text-xs text-center w-24" min={m.prev_black}
                            data-testid={`a4black-${m.machine_id}`} />
                        )}
                      </td>
                      {/* A4 COLOR */}
                      <td className="px-1 py-1 text-center text-xs text-gray-500 border-l">{m.prev_color.toLocaleString('el-GR')}</td>
                      <td className="px-1 py-1">
                        {isDone ? (
                          <div className="text-center font-mono text-xs text-green-700">{(v.a4_color || 0).toLocaleString('el-GR')}</div>
                        ) : (
                          <Input type="number" value={v.a4_color} placeholder={String(m.prev_color)}
                            onChange={e => handleChange(m.machine_id, 'a4_color', e.target.value)}
                            className="h-7 text-xs text-center w-24" min={m.prev_color}
                            data-testid={`a4color-${m.machine_id}`} />
                        )}
                      </td>
                      {/* A3 BLACK */}
                      <td className="px-1 py-1 text-center text-xs text-gray-500 border-l">{(m.prev_a3 || 0).toLocaleString('el-GR')}</td>
                      <td className="px-1 py-1">
                        {isDone ? (
                          <div className="text-center font-mono text-xs text-green-700">{(v.a3_black || 0).toLocaleString('el-GR')}</div>
                        ) : (
                          <Input type="number" value={v.a3_black} placeholder={String(m.prev_a3 || 0)}
                            onChange={e => handleChange(m.machine_id, 'a3_black', e.target.value)}
                            className="h-7 text-xs text-center w-24" min={m.prev_a3 || 0}
                            data-testid={`a3black-${m.machine_id}`} />
                        )}
                      </td>
                      {/* A3 COLOR */}
                      <td className="px-1 py-1 text-center text-xs text-gray-500 border-l">{(m.prev_a3_color || 0).toLocaleString('el-GR')}</td>
                      <td className="px-1 py-1">
                        {isDone ? (
                          <div className="text-center font-mono text-xs text-green-700">{(v.a3_color || 0).toLocaleString('el-GR')}</div>
                        ) : (
                          <Input type="number" value={v.a3_color} placeholder={String(m.prev_a3_color || 0)}
                            onChange={e => handleChange(m.machine_id, 'a3_color', e.target.value)}
                            className="h-7 text-xs text-center w-24" min={m.prev_a3_color || 0}
                            data-testid={`a3color-${m.machine_id}`} />
                        )}
                      </td>
                      {/* STATUS */}
                      <td className="px-2 py-1 text-center border-l">
                        {isDone ? (
                          <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-[10px] text-orange-500">ΕΚΚΡΕΜΕΙ</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="sticky bottom-0 md:bottom-0 py-3 bg-gray-50 border-t z-10 -mx-3 px-3 md:-mx-6 md:px-6">
        <Button onClick={handleSave} className="w-full bg-green-600 hover:bg-green-700 h-12 text-base"
          disabled={saving || pendingCount === 0} data-testid="save-bulk-btn">
          <Save className="h-5 w-5 mr-2" />
          {saving ? 'ΕΝΗΜΕΡΩΣΗ...' : `ΕΝΗΜΕΡΩΣΗ ΜΕΤΡΗΤΩΝ (${pendingCount} μηχανήματα)`}
        </Button>
      </div>
    </div>
  );
};

export default BulkReadingsPage;
